import React from "react";
import moment from "moment";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { CopyOutlineIcon, DownloadAltIcon, PencilIcon, CopyIcon, EyeIcon } from "../../icons";
import AdminBadge from "../_components/AdminBadge";
import { cellClass, stickyCellClass } from "../_components/AdminTable";
import AdminRowActions, { AdminIconButton } from "../_components/AdminRowActions";
import { isQuotationRetryableAfterCardChange } from "../../../utils/quotationCharge";

// Unsaved quotations have no Quotation No. or dates yet. Show "-" instead of
// a blank cell — and never call moment() on a missing date, because
// moment(undefined) is *today*, which would show a made-up date.
const EMPTY_CELL = "-";

const formatListDate = (date) =>
  date ? moment(date).locale("en").format("MMM Do, YYYY") : EMPTY_CELL;

const Item = ({ quotation, count }) => {
  const history = useHistory();

  const getStatusBadge = (quotation) => {
    // Keep this in sync with CreateQuotationPage.getStatusBadge so the list and
    // detail show the same status.
    // paymentStatus flow: NOT_PAID -> READY_TO_CHARGE -> CHARGE_INITIATED -> CHARGED
    // (CHARGE_FAILED on failure). PAID is legacy old data — already paid, terminal.
    if (quotation?.paymentStatus === "CHARGED") return { tone: "success", label: "Charged" };
    if (quotation?.paymentStatus === "CHARGE_INITIATED") return { tone: "info", label: "Charge Processing" };
    if (quotation?.paymentStatus === "READY_TO_CHARGE" || isQuotationRetryableAfterCardChange(quotation)) return { tone: "gold", label: "Ready To Charge" };
    if (quotation?.paymentStatus === "CHARGE_FAILED") return { tone: "danger", label: "Charge Failed" };
    if (quotation?.paymentStatus === "PAID") return { tone: "success", label: "Paid" };
    if (quotation?.quotationStatus === "UNSAVED") return { tone: "neutral", label: "Unsaved" };
    if (quotation?.quotationStatus === "DRAFT") return { tone: "neutral", label: "Draft" };
    if (quotation?.quotationStatus === "ACTIVE" && quotation?.paymentStatus === "NOT_PAID")
      return { tone: "info", label: "Active - Not paid" };
    if (quotation?.quotationStatus === "EXPIRED") return { tone: "danger", label: "Expired" };
    return { tone: "neutral", label: "-" };
  };

  const { tone: statusTone, label: statusLabel } = getStatusBadge(quotation);

  // Editable only while the customer hasn't authorized payment yet.
  const isPaymentLocked = Boolean(quotation?.paymentStatus) && quotation.paymentStatus !== "NOT_PAID";

  // A quotation without a saved link can't be shared or downloaded yet.
  const isUnshareable =
    quotation._id == null ||
    quotation?.quotationStatus === "UNSAVED" ||
    quotation?.quotationStatus === "DRAFT";

  const copyQuotationLink = () => {
    const textToCopy = `${process.env.REACT_APP_URL}/quotation/${quotation._id}`;

    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        alert("Quotation link copied to clipboard");
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
      });
  };

  return (
    <tr>
      <td className={cellClass}>{count + 1}</td>
      <td className={cellClass}>{quotation?.user?.name || EMPTY_CELL}</td>
      <td className={cellClass}>{quotation?.quotationID || EMPTY_CELL}</td>
      <td className={`${cellClass} tw:whitespace-nowrap`}>
        {`${(quotation?.totalAmount || 0).toLocaleString()} JPY ${quotation.useDiscount ? "(-" + quotation?.discount + "%)" : ""}`}
      </td>
      <td className={`${cellClass} tw:whitespace-nowrap`}>
        {formatListDate(quotation?.issueDate)}
      </td>
      <td className={`${cellClass} tw:whitespace-nowrap`}>
        {formatListDate(quotation?.expiredDate)}
      </td>
      <td className={cellClass}>
        <AdminBadge tone={statusTone}>{statusLabel}</AdminBadge>
      </td>
      <td className={stickyCellClass}>
        <AdminRowActions>
          <AdminIconButton
            tone="gold"
            label="Copy quotation link"
            disabled={isUnshareable}
            onClick={copyQuotationLink}
          >
            <CopyOutlineIcon size={16} />
          </AdminIconButton>
          <AdminIconButton
            tone="goldSoft"
            label="Download quotation"
            disabled={isUnshareable}
            onClick={() => history.push(`/admin/quotations/download/${quotation._id}`)}
          >
            <DownloadAltIcon />
          </AdminIconButton>
          <AdminIconButton
            tone="gold"
            label={isPaymentLocked ? "View quotation" : "Edit quotation"}
            onClick={() => history.push("/admin/quotations/create/" + quotation._id)}
          >
            {isPaymentLocked ? <EyeIcon size={16} color="#FFFFFF" /> : <PencilIcon color="#FFFFFF" size={14} />}
          </AdminIconButton>
          <AdminIconButton
            tone="goldSoft"
            label="Duplicate quotation"
            onClick={() => alert("DUPLICATE QUOTATION COMING SOON!")}
          >
            <CopyIcon size={16} />
          </AdminIconButton>
        </AdminRowActions>
      </td>
    </tr>
  );
};

export default Item;
